import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Download, RefreshCw, FileSearch, Save } from 'lucide-react';
import {
  ACCOUNTING_ROLES,
  BOOKKEEPING_EVIDENCE,
  type BookkeepingEvidence,
  type BookkeepingPacket,
  type BookkeepingPreview,
  type ConsignmentStatement,
} from '@tcvs/shared';
import { useConsignmentApi } from './consignment-api';
import { consignmentErrorMessage, formatMoneyMinor as money } from './consignment-ui';
import { bookkeepingCsv } from './bookkeeping-export';

const statusName = {
  PREPARED: 'Prepared for review',
  RECORDED: 'External entry recorded',
  CANCELED: 'Canceled',
};
export function ConsignmentBookkeeping({ consignorId }: { consignorId: string }): JSX.Element {
  const api = useConsignmentApi();
  const [statements, setStatements] = useState<ConsignmentStatement[]>([]);
  const [statementCursor, setStatementCursor] = useState<string>();
  const [packets, setPackets] = useState<BookkeepingPacket[]>([]);
  const [packetCursor, setPacketCursor] = useState<string>();
  const [selected, setSelected] = useState<string[]>([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState<BookkeepingPreview>();
  const [packet, setPacket] = useState<BookkeepingPacket>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const alive = useRef(true);
  const requestId = useRef<string>();
  async function run(action: () => Promise<void>) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : 'Bookkeeping unavailable.');
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function load(more?: 'statements' | 'packets') {
    if (!more || more === 'statements') {
      const result = await api.listStatements(consignorId, more ? statementCursor : undefined);
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      if (!alive.current) return;
      setStatements((current) => (more ? [...current, ...result.data.items] : result.data.items));
      setStatementCursor(result.data.cursor);
    }
    if (!more || more === 'packets') {
      const result = await api.listBookkeeping(consignorId, more ? packetCursor : undefined);
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      if (!alive.current) return;
      setPackets((current) => (more ? [...current, ...result.data.items] : result.data.items));
      setPacketCursor(result.data.cursor);
    }
  }
  useEffect(() => {
    alive.current = true;
    void run(() => load());
    return () => {
      alive.current = false;
    };
  }, [consignorId]); // Parent keys this component by consignor.
  function invalidate() {
    setPreview(undefined);
    requestId.current = undefined;
  }
  async function generate() {
    invalidate();
    setPacket(undefined);
    const result = await api.mutate<BookkeepingPreview>({
      operation: 'PREVIEW_BOOKKEEPING',
      consignorId,
      month,
      statementIds: selected,
    });
    if (!result.ok) throw new Error(consignmentErrorMessage(result));
    if (!alive.current) return;
    if (result.data.consignorId !== consignorId || result.data.month !== month)
      throw new Error('Packet did not match the selection.');
    setPreview(result.data);
    requestId.current = crypto.randomUUID();
  }
  async function prepare() {
    if (!preview || !requestId.current) return;
    const result = await api.mutate<BookkeepingPacket>({
      operation: 'PREPARE_BOOKKEEPING',
      consignorId,
      month,
      statementIds: selected,
      fingerprint: preview.fingerprint,
      requestId: requestId.current,
    });
    if (!result.ok) throw new Error(consignmentErrorMessage(result));
    if (!alive.current) return;
    setPacket(result.data);
    invalidate();
    await load();
  }
  async function record(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!packet) return;
    const form = new FormData(event.currentTarget);
    const evidence = Object.fromEntries(
      Object.keys(BOOKKEEPING_EVIDENCE).map((k) => [k, String(form.get(k) ?? '')]),
    ) as BookkeepingEvidence;
    await run(async () => {
      const result = await api.mutate<BookkeepingPacket>({
        operation: 'RECORD_BOOKKEEPING',
        consignorId,
        packetId: packet.packetId,
        company: String(form.get('company')),
        documentReference: String(form.get('documentReference')),
        postingDate: String(form.get('postingDate')),
        evidence,
        enteredExternally: form.get('enteredExternally') === 'on',
      });
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      if (!alive.current) return;
      setPacket(result.data);
      await load();
    });
  }
  async function cancel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!packet) return;
    const reason = String(new FormData(event.currentTarget).get('reason') ?? '');
    await run(async () => {
      const result = await api.mutate<BookkeepingPacket>({
        operation: 'CANCEL_BOOKKEEPING',
        consignorId,
        packetId: packet.packetId,
        reason,
      });
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      if (!alive.current) return;
      setPacket(result.data);
      await load();
    });
  }
  const report = packet ?? preview;
  function download(csv = false) {
    if (!report) return;
    const url = URL.createObjectURL(
      new Blob([csv ? bookkeepingCsv(report) : JSON.stringify(report, null, 2)], {
        type: csv ? 'text/csv;charset=utf-8' : 'application/json',
      }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `consignment-bookkeeping-${report.month}-${packet?.packetId ?? report.fingerprint.slice(0, 12)}.${csv ? 'csv' : 'json'}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section className="tcvs-accounting tcvs-bookkeeping" aria-label="Monthly bookkeeping">
      <h2>Monthly bookkeeping</h2>
      <p role="status">Manual review · No automatic posting or payments</p>
      {error && (
        <p className="tcvs-error" role="alert">
          {error}
        </p>
      )}
      <div className="tcvs-statement-toolbar">
        <label>
          Review month{' '}
          <input
            aria-label="Review month"
            type="month"
            max={new Date().toISOString().slice(0, 7)}
            value={month}
            disabled={busy}
            onChange={(e) => {
              setMonth(e.target.value);
              invalidate();
            }}
          />
        </label>
        <button
          title="Refresh statements and packets"
          aria-label="Refresh statements and packets"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              invalidate();
              setPacket(undefined);
              await load();
            })
          }
        >
          <RefreshCw size={18} />
        </button>
      </div>
      <h3>Approved statements</h3>
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>Select</th>
              <th>Statement</th>
              <th>Approved</th>
              <th>Original proceeds</th>
            </tr>
          </thead>
          <tbody>
            {statements
              .filter((s) => s.status === 'APPROVED')
              .map((s) => (
                <tr key={s.statementId}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select statement ${s.statementId}`}
                      checked={selected.includes(s.statementId)}
                      disabled={busy || (!selected.includes(s.statementId) && selected.length >= 8)}
                      onChange={(e) => {
                        setSelected(
                          e.target.checked
                            ? [...selected, s.statementId]
                            : selected.filter((id) => id !== s.statementId),
                        );
                        invalidate();
                      }}
                    />
                  </td>
                  <td>{s.statementId.slice(-8)}</td>
                  <td>{s.approvedAt?.slice(0, 10)}</td>
                  <td>{money(s.totals.estimatedProceedsMinor)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!statements.some((s) => s.status === 'APPROVED') && (
        <p>No approved statements on this page.</p>
      )}
      {statementCursor && (
        <button disabled={busy} onClick={() => void run(() => load('statements'))}>
          More statements
        </button>
      )}
      <div className="tcvs-statement-toolbar">
        <span>{selected.length} / 8 selected</span>
        <button disabled={busy || selected.length === 0} onClick={() => void run(generate)}>
          <FileSearch size={16} /> Preview packet
        </button>
        <button disabled={busy || !preview?.sources.length} onClick={() => void run(prepare)}>
          <Save size={16} /> Save review packet
        </button>
      </div>
      <h3>Saved packets</h3>
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>Packet</th>
              <th>Month</th>
              <th>Status</th>
              <th>Prepared</th>
              <th>External reference</th>
            </tr>
          </thead>
          <tbody>
            {packets.map((p) => (
              <tr key={p.packetId}>
                <td>
                  <button
                    className="tcvs-link-button"
                    disabled={busy}
                    onClick={() => {
                      invalidate();
                      setPacket(p);
                      setError(undefined);
                    }}
                  >
                    {p.packetId.slice(-8)}
                  </button>
                </td>
                <td>{p.month}</td>
                <td>{statusName[p.status]}</td>
                <td>{p.createdAt.slice(0, 10)}</td>
                <td>{p.recorded?.documentReference ?? 'Not recorded'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!packets.length && <p>No saved packets.</p>}
      {packetCursor && (
        <button disabled={busy} onClick={() => void run(() => load('packets'))}>
          More packets
        </button>
      )}
      {report && (
        <div className="tcvs-bookkeeping-report">
          <div className="tcvs-statement-toolbar">
            <h3>
              {packet ? statusName[packet.status] : 'Unsaved preview'} · {report.month}
            </h3>
            <button
              title="Download review packet JSON"
              aria-label="Download review packet JSON"
              onClick={() => download()}
              disabled={busy}
            >
              <Download size={18} />
            </button>
            <button
              title="Download review worksheet CSV"
              aria-label="Download review worksheet CSV"
              onClick={() => download(true)}
              disabled={busy}
            >
              <Download size={18} /> CSV
            </button>
          </div>
          <p>
            {report.sources.length} included events · {report.excluded.length} already reserved or
            recorded
          </p>
          <dl className="tcvs-detail-list">
            {Object.entries(report.totals).map(([role, amount]) => (
              <div key={role}>
                <dt>{ACCOUNTING_ROLES[role as keyof typeof ACCOUNTING_ROLES]}</dt>
                <dd>
                  {money(Math.abs(amount!))} {amount! < 0 ? 'credit' : 'debit'}
                </dd>
              </div>
            ))}
          </dl>
          {!!report.issues.length && (
            <>
              <h4>Review items</h4>
              <ul>
                {report.issues.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </>
          )}
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {report.sources.map((s) => (
                  <tr key={s.sourceId}>
                    <td>
                      {s.kind.replaceAll('_', ' ')}
                      <br />
                      <small className="tcvs-accounting-source">{s.sourceId}</small>
                    </td>
                    <td>
                      {s.date}
                      {s.priorPeriod ? ' (prior period)' : ''}
                    </td>
                    <td>{money(s.debitMinor)}</td>
                    <td>{s.paymentReference ?? s.statementId.slice(-8)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <details>
            <summary>Scope and assumptions</summary>
            <ul>
              {report.limitations.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </details>
          {!!report.excluded.length && (
            <details>
              <summary>Excluded source register</summary>
              <ul>
                {report.excluded.map((s) => (
                  <li key={s.sourceId}>
                    {s.sourceId} · packet {s.packetId}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {packet?.status === 'PREPARED' && (
            <>
              <form
                key={packet.packetId}
                onSubmit={(e) => void record(e)}
                className="tcvs-bookkeeping-form"
              >
                <h4>Record external bookkeeping entry</h4>
                <label>
                  Business Central company
                  <input name="company" minLength={3} maxLength={120} required disabled={busy} />
                </label>
                <label>
                  Posted document reference
                  <input
                    name="documentReference"
                    minLength={3}
                    maxLength={120}
                    required
                    disabled={busy}
                  />
                </label>
                <label>
                  Posting date
                  <input
                    name="postingDate"
                    type="date"
                    max={new Date().toISOString().slice(0, 10)}
                    required
                    disabled={busy}
                  />
                </label>
                {Object.entries(BOOKKEEPING_EVIDENCE).map(([k, label]) => (
                  <label key={k}>
                    {label}
                    <textarea
                      name={k}
                      minLength={3}
                      maxLength={500}
                      placeholder="Evidence reference or reviewed not-applicable reason"
                      required
                      disabled={busy}
                    />
                  </label>
                ))}
                <label>
                  <input type="checkbox" name="enteredExternally" required disabled={busy} /> I
                  reviewed these sources and recorded the entries outside TCVS.
                </label>
                <button disabled={busy}>Save external entry record</button>
              </form>
              <details>
                <summary>Cancel unentered packet</summary>
                <form className="tcvs-form-row" onSubmit={(e) => void cancel(e)}>
                  <label>
                    Cancellation reason
                    <input name="reason" minLength={3} maxLength={500} required disabled={busy} />
                  </label>
                  <button disabled={busy}>Cancel and release sources</button>
                </form>
              </details>
            </>
          )}
          {packet?.recorded && (
            <>
              <h4>External entry record (not verified with BC)</h4>
              <p>
                {packet.recorded.company} · {packet.recorded.documentReference} ·{' '}
                {packet.recorded.postingDate}
              </p>
              <dl>
                {Object.entries(packet.recorded.evidence).map(([k, v]) => (
                  <div key={k}>
                    <dt>{BOOKKEEPING_EVIDENCE[k as keyof typeof BOOKKEEPING_EVIDENCE]}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
          {packet?.canceled && (
            <p>
              Canceled {packet.canceled.at}: {packet.canceled.reason}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
